"use client";

// ─────────────────────────────────────────────────────────────────────────
// "Your payment needs attention" — a live subscription whose charge failed.
//
// Deliberately NOT part of PausedBanner, and deliberately not called "paused".
// That banner means the user CHOSE to pause their subscription (migration 015);
// it resumes on a date and its action is "Resume now". Here nobody chose
// anything, there is no resume date to promise, and the only thing that helps is
// a working card. One banner serving both would have to offer the wrong button
// to one of them.
//
// Why this exists at all: when a renewal charge fails, the renewal grant simply
// never runs (it is keyed to invoice.payment_succeeded), so the account quietly
// stops being topped up. Everything the user sees after that — the credit bar,
// the upgrade prompts — reads like ordinary exhaustion, because in our tables it
// is indistinguishable from it. This is the one surface that says the true
// thing. Nothing is switched off: the plan, the balance and every gate are
// untouched while Stripe retries.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { CreditCard, AlertTriangle, X } from "lucide-react";
import {
  trackPaymentIssueBannerShown,
  trackPaymentIssueFixClicked,
} from "@/lib/analytics";

/**
 * Straight into the card form, not just onto the billing page.
 *
 * Without the param this lands on Settings and leaves the user to find the
 * Payment details section and press Update — two unhighlighted steps on a page
 * that also sells plan changes and top-ups. PaymentDetailsCard reads this and
 * opens the form on arrival. (The "Update payment method" button further up that
 * same page is NOT this: despite the label it opens the change-plan modal.)
 */
const BILLING_HREF = "/settings/billing?action=update-card";

interface PaymentIssueBannerProps {
  /**
   * Pass it when the host already holds the answer (/api/credits →
   * `payment_issue`). Omit it and the banner asks /api/billing/payment-state
   * itself — the same self-fetching shape PausedBanner uses, and the reason this
   * can be dropped onto a page without that page threading billing state
   * through its own credit fetch.
   *
   * Either way the SERVER decided: both sources run shouldShowPaymentIssue, so
   * this component never re-derives the policy. A second opinion here would just
   * be a second thing to keep in sync.
   */
  paymentIssue?: boolean | null;
  /** Analytics attribution only: which screen the banner rendered on. */
  surface: string;
}

export function PaymentIssueBanner({
  paymentIssue,
  surface,
}: PaymentIssueBannerProps) {
  const [fetched, setFetched] = useState<boolean | null>(null);
  const provided = paymentIssue !== undefined && paymentIssue !== null;

  useEffect(() => {
    if (provided) return;
    let live = true;

    const read = () => {
      fetch("/api/billing/payment-state", { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (live && d) setFetched(Boolean(d.paymentIssue));
        })
        .catch(() => {
          // Silent: a banner that fails to load is a non-event, and the webhook
          // re-asserts the state on Stripe's next retry.
        });
    };

    read();

    // Re-read when the tab comes back to the foreground. The recovery path this
    // exists for is the one the banner itself sends people down: click "Update
    // payment method", fix the card in Settings (or Stripe's own portal, in
    // another tab), come back. Stripe retries the open invoice as soon as a
    // usable card is attached, so by the time they return the account is often
    // already fixed — and a mount-once fetch would keep telling them it isn't
    // until they happen to reload. Cheap: a two-column read, only on focus.
    const onFocus = () => {
      if (document.visibilityState === "visible") read();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      live = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [provided]);

  const issue = provided ? Boolean(paymentIssue) : Boolean(fetched);

  // Dismissible for THIS page view only, and never persisted. The account keeps
  // missing its renewal until the card is fixed, so the notice comes back on the
  // next navigation — it just must not sit permanently over the work in between.
  // Scoping it to the mount is also what keeps this honest without extra
  // machinery: there is no dismissal to expire, because it dies with the page.
  const [dismissed, setDismissed] = useState(false);
  const seen = useRef(false);

  const visible = issue && !dismissed;

  // One impression per issue, gated on a ref rather than the effect's deps so a
  // re-render (a credits poll, or a focus re-read returning the same value)
  // cannot re-fire it. Reset above when the account recovers.
  useEffect(() => {
    if (!visible || seen.current) return;
    seen.current = true;
    trackPaymentIssueBannerShown({ surface });
  }, [visible, surface]);

  // Tell the page a bar is occupying the top of the viewport. Two rules in
  // globals.css read this:
  //
  //   • the sticky headers below drop by --top-banner-h, so this can be sticky
  //     TOO and the two ride down the page as one block. Without that the banner
  //     simply scrolled away — it is in normal flow while every header here is
  //     sticky — and the one explanation for an account running out of credits
  //     vanished the moment the user scrolled to the work.
  //   • the homepage header row grows, because it is a fixed 58px with its
  //     contents centred and that leaves the Panda logo ~9px of clearance:
  //     ample under open hero, tight the moment anything sits above it.
  //
  // The height is MEASURED, not assumed. This banner wraps to two lines on a
  // phone, so any hard-coded offset would be wrong on exactly the screens where
  // a header overlapping the content hurts most.
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!visible) return;
    const root = document.documentElement;
    root.setAttribute("data-top-banner", "");

    const el = barRef.current;
    const sync = () => {
      root.style.setProperty(
        "--top-banner-h",
        `${Math.round(el?.getBoundingClientRect().height ?? 0)}px`,
      );
    };
    sync();
    // Guarded: ResizeObserver is absent in jsdom and in older browsers, and the
    // banner must still render there — it just stops re-measuring on a reflow.
    // The one-off sync above has already set a usable height.
    const ro =
      el && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(sync)
        : null;
    ro?.observe(el as Element);

    return () => {
      ro?.disconnect();
      root.removeAttribute("data-top-banner");
      root.style.removeProperty("--top-banner-h");
    };
  }, [visible]);

  // The banner has no grid of its own — it borrows whichever header the page it
  // was dropped onto actually has, and those differ. The homepage nav is
  // max-w-[1080px] px-3 sm:px-6 (home-client.tsx); AppHeader and the <main> on
  // /projects and /profile are max-w-6xl px-4 sm:px-6. A single width lined up
  // on one page and was visibly off on the other two, which is what `surface`
  // now decides in addition to analytics attribution.
  const grid =
    surface === "home"
      ? "max-w-[1080px] px-3 sm:px-6"
      : "max-w-6xl px-4 sm:px-6";

  if (!visible) return null;

  return (
    // STICKY, and part of the header stack rather than a thing sitting on top of
    // it. Every header on these pages is sticky (home's nav, AppHeader), so a
    // banner left in normal flow simply scrolled away — taking the only
    // explanation for an account running out of credits with it, at exactly the
    // moment the user scrolls down to the work and hits the wall. The headers
    // drop by --top-banner-h (globals.css), so the two ride together.
    //
    // z-[55] clears home's nav at z-50 and AppHeader at z-40, and — the reason it
    // exists at all — the homepage's ABSOLUTE full-viewport hero halo, which in
    // CSS paints over any static sibling whatever the source order. Without a
    // stacking position the banner rendered, took its space, and was invisible.
    //
    // The wash is amber-500/15, the house spelling for a warning surface here
    // (bg-amber-500/[0.06–0.15] appears ~45 times) rather than the opaque fill
    // this started as. An opaque bar re-created the exact "white strip" the
    // homepage nav's own comment records as rejected feedback, cutting the hero
    // gradient dead. Blur stays because this is sticky now: content genuinely
    // does scroll behind it.
    <div
      ref={barRef}
      role="status"
      // OPAQUE. It was a translucent wash back when it scrolled with the page;
      // now that it is sticky, the whole page runs underneath it, and a
      // see-through bar means text sliding through text. The dark value is the
      // composite the wash used to produce (amber-500 at 15% over the app
      // background), so it is the same colour, just no longer letting anything
      // through it.
      className="sticky top-0 z-[55] w-full border-b border-amber-500/30 bg-amber-100 dark:border-amber-500/20 dark:bg-[#2A1F11]"
    >
      <div
        className={`mx-auto flex min-h-[48px] w-full flex-wrap items-start gap-x-3 gap-y-2 py-2.5 md:flex-nowrap md:items-center md:gap-x-4 ${grid}`}
      >
        {/* amber-700 in light, not amber-600: on this wash amber-600 measures
            about 2.6:1, under the 3:1 WCAG 1.4.11 asks of a glyph carrying
            meaning. Dark keeps amber-400. */}
        <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-amber-700 md:mt-0 dark:text-amber-400" />

        <p className="min-w-0 flex-1 text-sm leading-snug text-amber-800 dark:text-amber-200">
          <span className="font-semibold text-amber-900 dark:text-amber-100">
            Your last payment didn&rsquo;t go through.
          </span>{" "}
          {/* NOT hidden on mobile any more. Hiding it left a phone reading only
              "your last payment didn't go through" — precisely the framing that
              makes someone assume the plan is already gone, and someone who
              believes that has no reason to fix a card. It wraps instead.

              "monthly credits", not "new credits": a top-up is a separate
              purchase and does NOT clear this state, so telling someone who has
              just bought a credit pack that "new credits are paused" reads as a
              billing bug rather than as a message about their renewal. */}
          <span>
            Your plan is still active &mdash; only your monthly credits are
            paused.
          </span>
        </p>

        {/* w-full below md so the actions deterministically take their own line:
            the copy can never crush the button, nor the button the copy. pl-7 =
            icon (16px) + gap-x-3 (12px), so on a phone the row hangs under the
            text rather than under the icon. */}
        <div className="flex w-full shrink-0 items-center gap-1 pl-7 md:w-auto md:pl-0">
          {/* Emerald, not amber. PANDA-1779 settled this on the grow canvas
              (globals.css): an amber CTA beside an amber warning makes the
              problem and the way out indistinguishable. It also separates this
              from PausedBanner's amber "Resume now" pill, which can sit directly
              above it. .bg-panda-gradient-success is documented there as
              5.4:1–7.6:1 for white label text, and 4.6:1 under its hover lift. */}
          <a
            href={BILLING_HREF}
            onClick={() => trackPaymentIssueFixClicked({ surface })}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-panda-gradient-success px-4 text-xs font-semibold text-white shadow-sm shadow-emerald-900/25 transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Update payment method
          </a>
          {/* An icon, not the word "Later": at this size a second worded button
              competes with the one action that matters, and "Later" oversells a
              dismissal that dies with the page. */}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            title="Hide until the next page"
            className="ml-auto rounded-full p-1.5 text-amber-700/70 transition-colors hover:bg-amber-500/15 hover:text-amber-900 md:ml-0 dark:text-amber-300/70 dark:hover:text-amber-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

}
